import React, { useState } from "react";
import { Home, Gem, Headset } from "lucide-react";
import { FaBars } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { Routes } from "../../routes/baseRoutes";
import MoreOverlay from "./more-overlay";
import NotificationButton from "../NotificationButton";
import { useNavigation } from "../../hooks/useNavigation";
import { Z_INDEX } from "../../constants/layout";
import { useEventPlannerPath } from "../../hooks/useEventPlannerPath";

const MobileNav = ({
  activeTab = "dashboard",
  onTabChange,
}: {
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}) => {
  const [active, setActive] = useState(activeTab);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const navigate = useNavigate();
  const { closeDrawersExcept } = useNavigation();
  
  const { isEventPlanner, basePath } = useEventPlannerPath();

  const navItems = [
    {
      id: "home",
      icon: Home,
      label: "Home",
      route: isEventPlanner ? basePath : Routes.index,
    },
    {
      id: "weddnpro",
      icon: Gem,
      label: "WeddnPro",
      route: isEventPlanner
        ? basePath + Routes.weddnProSubscription
        : Routes.weddnProSubscription,
    },
    {
      id: "notifications",
      icon: "NotificationButton",
      label: "Notifications",
      route: null,
    },
    {
      id: "support",
      icon: Headset,
      label: "Support",
      route: isEventPlanner ? basePath + Routes.support : Routes.support,
    },
    { id: "more", icon: FaBars, label: "More", route: null },
  ];

  const handleTabClick = (id: string, route: string | null) => {
    setActive(id);
    if (onTabChange) {
      onTabChange(id);
    }

    // Close drawers when navigating to other tabs
    if (id !== "notifications") {
      closeDrawersExcept(id);
    }

    if (id === "more") {
      setIsOverlayOpen(true);
    } else if (id === "notifications") {
      // NotificationButton handles its own action (opening drawer)
      // We just need to set the active state
    } else if (route) {
      navigate(route);
    }
  };

  const handleNotificationEvent = (eventData: {
    action: "opened" | "closed";
    from: string;
  }) => {
    if (eventData.action === "opened") {
      setActive("notifications");
    }
  };

  return (
    <>
      <div
        className={`fixed bottom-0 left-0 right-0 bg-black/70 backdrop-blur-lg border-t border-gray-200 glacial md:hidden`}
        style={{ zIndex: Z_INDEX.navigation }}
      >
        <nav className="flex justify-around items-center h-20 px-2 pb-3">
          {navItems.map((item) => {
            const isActive = active === item.id;

            if (item.icon === "NotificationButton") {
              return (
                <NotificationButton
                  key={item.id}
                  size="sm"
                  showLabel={true}
                  label={item.label}
                  className="flex-1 h-full flex items-center justify-center"
                  buttonClassName={`transition-colors ${
                    isActive ? "text-white" : "text-gray-400"
                  } hover:bg-transparent hover:text-current focus:ring-0`}
                  iconClassName={`w-6 h-6 ${isActive ? "text-white" : "text-gray-400"}`}
                  labelClassName={`font-[glacial] ${
                    isActive
                      ? "font-medium text-white"
                      : "font-normal text-gray-400"
                  }`}
                  strokeWidth={isActive ? 1.2 : 0.5}
                  isMobile={true}
                  onNavigationEvent={handleNotificationEvent}
                />
              );
            }

            const Icon = item.icon as any;
            return (
              <button
                key={item.id}
                onClick={() => handleTabClick(item.id, item.route)}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  isActive ? "text-white" : "text-gray-400"
                }`}
              >
                <Icon size={24} strokeWidth={isActive ? 1.2 : 0.5} />
                <span
                  className={`text-xs mt-1 font-[glacial] ${
                    isActive ? "font-medium" : "font-normal"
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <MoreOverlay
        isOpen={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
      />
    </>
  );
};

export default MobileNav;
